// gibbs-self-hosted-substructure-replace Path A (2026-05-15):
// The MinimalLib is the @rdkit/rdkit v2025.3.4 build (with `get_rxn` +
// JSReaction.run_reactants for client-side substructure replacement).
// The blob is copied from node_modules into src/plugin/ at install
// time by `npm run sync:rdkit` (postinstall hook) — webpack/Next play
// poorly with .wasm imports from node_modules. Bundle hit is ~2 MB.
import initRDKitModule from './RDKit_minimal';
import RDKitModuleWasm from './RDKit_minimal.wasm';

const USE_COORDGEN = true;
let RDKitModule;
initRDKitModule({
    locateFile(path) {
        if (path.endsWith('.wasm')) return RDKitModuleWasm;
        return path;
    },
    onRuntimeInitialized() { // TODO make loading screen wait for this
        console.log('RDKit runtime initialized');
    },
}).then(
    (instance) => { RDKitModule = instance; },
    (error) => { console.error(`RDKit runtime failed to initialize: ${error}`); },
);

export class RDKitExport {
    static isInitialized() {
        return Boolean(RDKitModule);
    }

    static getMol(data) {
        if (!RDKitModule) throw new Error('RDKit not initialized');
        return RDKitModule.get_mol(data);
    }

    // I'm not clear what this is for'
    static getQMol(data) {
        if (!RDKitModule) throw new Error('RDKit not initialized');
        return RDKitModule.get_qmol(data);
    }

    // getCleanMol takes molblock input and returns a mol object flattened and without hydrogens.
    // Note 2026-05-15: the upstream `@rdkit/rdkit` MinimalLib renamed `remove_hs()` to
    // `remove_hs_in_place()` (mutates) — we keep the previous behavior by returning the same
    // object after the in-place removal.
    static getCleanMol(data) {
        if (!RDKitModule) throw new Error('RDKit not initialized');
        let molA = RDKitExport.getMol(data);
        let molB = RDKitExport.getMol(molA.get_new_coords(USE_COORDGEN));
        molA.delete();
        molB.remove_hs_in_place();
        molA = RDKitExport.getMol(molB.get_kekule_form());
        molB.delete();
        return molA;
    }

    /**
     * Run a reaction SMARTS against a SMILES input and return the unique
     * product SMILES set. Backs the client-side substructure-replacement
     * path that replaces the previously Dan-owned
     * `/services/medchem/replace-substructure/` endpoint.
     *
     * The reaction SMARTS uses atom-mapping numbers (e.g. `[CX3:1](=O)...`)
     * to relate reactant atoms to product atoms. See `compoundTransform.js`
     * for the chemistry that derives the reaction SMARTS from a rule's
     * `reactionSmarts` field.
     *
     * @param {string} smiles - Input compound SMILES.
     * @param {string} reactionSmarts - Reaction SMARTS with atom maps.
     * @param {{ maxResults?: number }} [opts]
     * @returns {Array<string>} unique product SMILES; empty when no
     *   substructure match or RDKit isn't initialized.
     */
    static runReaction(smiles, reactionSmarts, opts = {}) {
        if (!RDKitModule) return [];
        if (!smiles || !reactionSmarts) return [];
        const maxResults = opts.maxResults ?? 12;

        let mol = null;
        let rxn = null;
        let reactants = null;
        let products = null;
        const out = new Set();
        try {
            mol = RDKitModule.get_mol(smiles);
            if (!mol || !mol.is_valid()) return [];
            rxn = RDKitModule.get_rxn(reactionSmarts);
            if (!rxn) return [];
            reactants = new RDKitModule.MolList();
            reactants.append(mol);
            products = rxn.run_reactants(reactants);
            const matchCount = products.size();
            for (let i = 0; i < matchCount; i += 1) {
                const productSet = products.get(i);
                const numProducts = productSet.size();
                for (let j = 0; j < numProducts; j += 1) {
                    const pmol = productSet.at(j);
                    try {
                        const psmi = pmol.get_smiles();
                        if (psmi && !out.has(psmi)) out.add(psmi);
                    } catch (e) {
                        // Skip unrenderable product (sanitization failure,
                        // valence error, etc.).
                    }
                    if (out.size >= maxResults) break;
                }
                if (out.size >= maxResults) break;
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[RDKitExport.runReaction] error:', err?.message);
        } finally {
            if (products && products.delete) products.delete();
            if (reactants && reactants.delete) reactants.delete();
            if (rxn && rxn.delete) rxn.delete();
            if (mol && mol.delete) mol.delete();
        }
        return Array.from(out);
    }

    /** This functions checks the orientation of the given molblock molecule.
     * If it is more "tall" than "wide", rotate it 90 degrees
     */
    static molSpin(molBlock) {
        const x = [];
        const y = [];
        let index = molBlock.indexOf('.');

        // Each '.' in a mol corresponds to a coordinate. Pull x and y coordinates into the arrays.
        while (index > 0) {
            x.push(parseFloat(molBlock.substr(index - 5, 10), 10));
            index += 10;
            y.push(parseFloat(molBlock.substr(index - 5, 10), 10));
            index = molBlock.indexOf('.', index + 11);
        }

        // if the arrays are empty, we don't need to rotate
        if (x.length === 0 || y.length === 0) return 0;
        const xdelta = x.reduce((a, b) => Math.max(a, b)) - x.reduce((a, b) => Math.min(a, b));
        const ydelta = y.reduce((a, b) => Math.max(a, b)) - y.reduce((a, b) => Math.min(a, b));
        return (ydelta > xdelta ? 90 : 0);
    }
}
